import {usePermissionStore} from '../store/permissionStore';
import {
  CreatePermissionRequest,
  UpdatePermissionRequest,
  PermissionFilters,
  AssignRolePermissionRequest,
  BulkAssignRolePermissionsRequest,
  AssignUserPermissionRequest,
  DenyUserPermissionRequest,
  Permission,
  UserEffectivePermissions
} from '../models/permission.model';
import {useCallback} from 'react';

export const usePermissions = () => {
  const {
    // State
    permissions,
    rolePermissions,
    userEffectivePermissions,
    pagination,
    loading,
    creating,
    updating,
    deleting,
    assigning,
    error,
    createError,
    updateError,
    deleteError,
    assignError,
    filters,

    // Actions
    fetchPermissions,
    fetchAllPermissions,
    createPermission,
    updatePermission,
    deletePermission,
    assignRolePermission,
    bulkAssignRolePermissions,
    getRolePermissions,
    removeRolePermission,
    bulkRemoveRolePermissions,
    assignUserPermission,
    denyUserPermission,
    getUserEffectivePermissions,
    setFilters,
    clearError,
    clearCreateError,
    clearUpdateError,
    clearDeleteError,
    clearAssignError,
    reset
  } = usePermissionStore();

  // Enhanced actions with error handling
  const handleFetchPermissions = useCallback(async (newFilters?: PermissionFilters) => {
    try {
      await fetchPermissions(newFilters);
    } catch (error) {
      throw error;
    }
  }, [fetchPermissions]);

  const handleCreatePermission = useCallback(async (permissionData: CreatePermissionRequest): Promise<Permission> => {
    try {
      const newPermission = await createPermission(permissionData);
      return newPermission;
    } catch (error) {
      throw error;
    }
  }, [createPermission]);

  const handleUpdatePermission = useCallback(async (permissionData: UpdatePermissionRequest): Promise<Permission> => {
    try {
      const updatedPermission = await updatePermission(permissionData);
      return updatedPermission;
    } catch (error) {
      throw error;
    }
  }, [updatePermission]);

  const handleDeletePermission = useCallback(async (permissionId: number): Promise<void> => {
    try {
      await deletePermission(permissionId);
    } catch (error) {
      throw error;
    }
  }, [deletePermission]);

  const handleAssignRolePermission = useCallback(async (assignmentData: AssignRolePermissionRequest): Promise<any> => {
    try {
      const result = await assignRolePermission(assignmentData);
      return result;
    } catch (error) {
      throw error;
    }
  }, [assignRolePermission]);

  const handleBulkAssignRolePermissions = useCallback(async (assignmentData: BulkAssignRolePermissionsRequest): Promise<any> => {
    try {
      const result = await bulkAssignRolePermissions(assignmentData);
      return result;
    } catch (error) {
      throw error;
    }
  }, [bulkAssignRolePermissions]);

  const handleGetRolePermissions = useCallback(async (roleId: number): Promise<Permission[]> => {
    try {
      const permissions = await getRolePermissions(roleId);
      return permissions;
    } catch (error) {
      throw error;
    }
  }, [getRolePermissions]);

  const handleRemoveRolePermission = useCallback(async (roleId: number, permissionId: number): Promise<void> => {
    try {
      await removeRolePermission(roleId, permissionId);
    } catch (error) {
      throw error;
    }
  }, [removeRolePermission]);

  const handleBulkRemoveRolePermissions = useCallback(async (assignmentData: BulkRemoveRolePermissionsRequest): Promise<void> => {
    try {
      await bulkRemoveRolePermissions(assignmentData);
    } catch (error) {
      throw error;
    }
  }, [bulkRemoveRolePermissions]);

  const handleAssignUserPermission = useCallback(async (assignmentData: AssignUserPermissionRequest): Promise<any> => {
    try {
      const result = await assignUserPermission(assignmentData);
      return result;
    } catch (error) {
      throw error;
    }
  }, [assignUserPermission]);

  const handleDenyUserPermission = useCallback(async (denialData: DenyUserPermissionRequest): Promise<any> => {
    try {
      const result = await denyUserPermission(denialData);
      return result;
    } catch (error) {
      throw error;
    }
  }, [denyUserPermission]);

  const handleGetUserEffectivePermissions = useCallback(async (userId: number): Promise<UserEffectivePermissions> => {
    try {
      const effectivePermissions = await getUserEffectivePermissions(userId);
      return effectivePermissions;
    } catch (error) {
      throw error;
    }
  }, [getUserEffectivePermissions]);

  // Enhanced filter handling
  const handleSetFilters = useCallback((newFilters: Partial<PermissionFilters>) => {
    setFilters(newFilters);
  }, [setFilters]);

  // Utility functions
  const getPermissionsByResource = useCallback((resource: string): Permission[] => {
    return permissions.filter(permission => permission.resource === resource);
  }, [permissions]);

  const getPermissionsByAction = useCallback((action: string): Permission[] => {
    return permissions.filter(permission => permission.action === action);
  }, [permissions]);

  const hasPermission = useCallback((userId: number, permissionName: string): boolean => {
    const userPermissions = userEffectivePermissions[userId];
    if (!userPermissions) {return false;}

    return userPermissions.effectivePermissions.some(
      permission => permission.name === permissionName
    );
  }, [userEffectivePermissions]);

  const getRolePermissionsById = useCallback((roleId: number): Permission[] => {
    return rolePermissions[roleId] || [];
  }, [rolePermissions]);

  const getUserEffectivePermissionsById = useCallback((userId: number): UserEffectivePermissions | null => {
    return userEffectivePermissions[userId] || null;
  }, [userEffectivePermissions]);

  return {
    // State
    permissions,
    rolePermissions,
    userEffectivePermissions,
    pagination,
    loading,
    creating,
    updating,
    deleting,
    assigning,
    error,
    createError,
    updateError,
    deleteError,
    assignError,
    filters,

    // Enhanced Actions
    fetchPermissions: handleFetchPermissions,
    fetchAllPermissions,
    createPermission: handleCreatePermission,
    updatePermission: handleUpdatePermission,
    deletePermission: handleDeletePermission,
    assignRolePermission: handleAssignRolePermission,
    bulkAssignRolePermissions: handleBulkAssignRolePermissions,
    getRolePermissions: handleGetRolePermissions,
    removeRolePermission: handleRemoveRolePermission,
    bulkRemoveRolePermissions: handleBulkRemoveRolePermissions,
    assignUserPermission: handleAssignUserPermission,
    denyUserPermission: handleDenyUserPermission,
    getUserEffectivePermissions: handleGetUserEffectivePermissions,
    setFilters: handleSetFilters,

    // Utility Actions
    clearError,
    clearCreateError,
    clearUpdateError,
    clearDeleteError,
    clearAssignError,
    reset,

    // Helper Functions
    getPermissionsByResource,
    getPermissionsByAction,
    hasPermission,
    getRolePermissionsById,
    getUserEffectivePermissionsById
  };
};
